// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import { action } from 'mobx';
import Asset, { AssetDTO } from '@shared/models/asset';
import BaseStore from '@core/stores/base';
import container from '@core/di';
import AssetsService from '@shared/services/assets';
import { AddPanelDTO, UpdateLatticeTowerMeasurementDTO, UpdateMemberDTO, UpdatePanelDTO } from '@shared/models/measurement-models';
import { UpdateEquipmentDTO } from '@shared/models/windarea';

@injectable()
export default class AssetsStore extends BaseStore<AssetDTO, Asset, AssetsService> {
  static diToken = Symbol('assets-store');
  constructor() {
    super({ service: container.get<AssetsService>(AssetsService.diToken) });
  }
  
  // Get info for a list of assets in an order
  @action getAssetListInfo = async (assetIds: number[]) => {
    //console.log('getAssetsInfo assetIds ', assetIds)
    this.loading.item = true;
    try {
      const assets = await this.service.getAssetsFullInfo(assetIds);
      this.loading.item = false;

      return assets;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  // Get lattice tower measurement info for a list of assets in an order
  @action getLatticeAssetsTowerMeasurement = async (assetIds: number[]) => {
    //console.log('getLatticeAssetsTowerMeasurement assetIds ', assetIds)
    this.loading.item = true;
    try {
      const assets = await this.service.getLatticeAssetsTowerMeasurement(assetIds);
      this.loading.item = false;

      return assets;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  // Get lattice tower measurement info for a list of assets in an order
  @action getTowerEquipments = async (assetIds: number[]) => {
    //console.log('getLatticeAssetsTowerMeasurement assetIds ', assetIds)
    this.loading.item = true;
    try {
      const assets = await this.service.getTowerEquipments(assetIds);
      this.loading.item = false;

      return assets;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  // Update lattice tower measurement info
  @action updateLatticeTowerMeasurement = async (latticeTowerMeasurement: Array<UpdateLatticeTowerMeasurementDTO>): Promise<Array<number>> => {
    //console.log('updateLatticeTowerMeasurement:', latticeTowerMeasurement)
    this.loading.item = true;
    try {
      const failedIds = await this.service.updateLatticeTowerMeasurement(latticeTowerMeasurement);
      this.loading.item = false;

      return failedIds;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  @action updateLatticeTowerPanel = async (panelDTOs: Array<UpdatePanelDTO>): Promise<Array<number>> => {
    //console.log('updateLatticeTowerPanel:', panelDTOs)
    this.loading.item = true;
    try {
      const failedIds = await this.service.updateLatticeTowerPanel(panelDTOs);
      this.loading.item = false;

      return failedIds;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  @action updateLatticeTowerPanelMembers = async (memberDTOs: Array<UpdateMemberDTO>): Promise<Array<number>> => {
    //console.log('updateLatticeTowerPanelMembers:', memberDTOs)
    this.loading.item = true;
    try {
      const failedIds = await this.service.updateLatticeTowerPanelMembers(memberDTOs);
      this.loading.item = false;

      return failedIds;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  @action addLatticeTowerPanel = async (panelDTO: AddPanelDTO): Promise<number> => {
    //console.log('addLatticeTowerPanel:', panelDTO)
    this.loading.item = true;
    try {
      const newId = await this.service.addLatticeTowerPanel(panelDTO);
      this.loading.item = false;

      return newId;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  @action deleteLatticeTowerPanels = async (toBeDeletedIds: Array<number>): Promise<Array<number>> => {
    //console.log('deleteLatticeTowerPanel:', toBeDeletedIds)
    this.loading.item = true;
    try {
      const deletedIds = await this.service.deleteLatticeTowerPanels(toBeDeletedIds);
      this.loading.item = false;

      return deletedIds;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  @action addLatticeTowerPanelMember = async (memberDTO: UpdateMemberDTO): Promise<number> => {
    //console.log('addLatticeTowerPanelMember:', memberDTO)
    this.loading.item = true;
    try {
      const newId = await this.service.addLatticeTowerPanelMember(memberDTO);
      this.loading.item = false;

      return newId;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  @action deleteLatticeTowerPanelMembers = async (toBeDeletedIds: Array<number>): Promise<Array<number>> => {
    //console.log('deleteLatticeTowerPanelMembers:', toBeDeletedIds)
    this.loading.item = true;
    try {
      const deletedIds = await this.service.deleteLatticeTowerPanelMembers(toBeDeletedIds);
      this.loading.item = false;

      return deletedIds;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  // Get lattice tower measurement info for a list of assets in an order
  @action getLatticeAssetsPanels = async (assetIds: number[]) => {
    //console.log('getLatticeAssetsPanels assetIds ', assetIds)
    this.loading.item = true;
    try {
      const assets = await this.service.getLatticeAssetsPanels(assetIds);
      this.loading.item = false;

      return assets;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  // Add tower equipment
  @action addTowerEquipment = async (equipmentDTO: UpdateEquipmentDTO): Promise<number> => {
    //console.log('addTowerEquipment:', equipmentDTO)
    this.loading.item = true;
    try {
      const newId = await this.service.addTowerEquipment(equipmentDTO);
      this.loading.item = false;

      return newId;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  // Delete a list of equipments
  @action deleteTowerEquipments = async (toBeDeletedIds: Array<number>): Promise<Array<number>> => {
    //console.log('deleteTowerEquipments:', toBeDeletedIds)
    this.loading.item = true;
    try {
      const deletedIds = await this.service.deleteTowerEquipments(toBeDeletedIds);
      this.loading.item = false;

      return deletedIds;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  // Update a list of equipments
  @action updateTowerEquipments = async (equipmentDTOs: Array<UpdateEquipmentDTO>): Promise<Array<number>> => {
    //console.log('updateTowerEquipments:', memberDTOs)
    this.loading.item = true;
    try {
      const failedIds = await this.service.updateTowerEquipments(equipmentDTOs);
      this.loading.item = false;

      return failedIds;
    } 
    catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  // Call service to get list of site types.
  getSiteTypes = async (): Promise<Array<{id: number, name: string}>> => {
    try {
      const siteTypes = await this.service.getSiteTypes();

      return siteTypes;
    } 
    catch (err) {
      throw err;
    }
  }  
}